import { Card, CardHeader, CardTitle, CardBody, Skeleton, Badge } from '@vnetwork/ui/primitives';
import { EmptyState, Timeline, type TimelineItem } from '@vnetwork/ui/data';
import { History } from 'lucide-react';
import { formatDateTime } from '@/lib/format';
import { useServiceUpdates } from '@/api/services';

/**
 * Service activity log ("Lịch sử thao tác") — the change-history timeline shown
 * at the bottom of the Information tab on every service (legacy `ServiceUpdates`).
 * Renders `/services/:id/updates` as a dated timeline: who, what action, description.
 */
export function ServiceActivityLog({ id }: { id: number }) {
  const { data, isLoading } = useServiceUpdates(id);
  const rows = data?.results ?? [];

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Activity log</CardTitle>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 40 }} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={<History size={24} />} title="No activity" description="No changes have been recorded for this service yet." />
        ) : (
          <Timeline
            size="sm"
            items={rows.map<TimelineItem>((row, i) => ({
              id: i,
              tone: 'primary',
              title: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.employee_name || 'System'}</span>
                  {row.action ? <Badge tone="neutral" appearance="subtle" size="sm">{row.action}</Badge> : null}
                </span>
              ),
              timestamp: <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-xs)' }}>{formatDateTime(row.created_at)}</span>,
              description: row.description ? (
                <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', overflowWrap: 'anywhere' }}>
                  {row.description}
                </span>
              ) : undefined,
            }))}
          />
        )}
      </CardBody>
    </Card>
  );
}
